import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { api, onUnauthorized, session, unwrap } from '../api/client';
import type { Me } from '../api/types';

interface SessionState {
  readonly me: Me | null;
  readonly loading: boolean;
  /** The session could not be checked for a reason other than an invalid token (API down, 5xx). */
  readonly unavailable: boolean;
  readonly can: (permission: string) => boolean;
  readonly refresh: () => Promise<void>;
  readonly logout: () => Promise<void>;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const token = session.get();
  const me = useQuery({
    queryKey: ['me', token],
    queryFn: () => unwrap(api.get<Me>('/portal/me')),
    enabled: token !== null,
    retry: false,
  });
  useEffect(
    () =>
      onUnauthorized(() => {
        session.clear();
        qc.clear();
        navigate('/login', { replace: true });
      }),
    [navigate, qc],
  );
  const value: SessionState = {
    me: me.data ?? null,
    loading: token !== null && me.isPending,
    unavailable: token !== null && me.isError && (me.error as { status?: number }).status !== 401,
    can: (p) => me.data?.permissions.includes(p) ?? false,
    refresh: async () => {
      await qc.invalidateQueries({ queryKey: ['me'] });
    },
    logout: async () => {
      await api.post('/auth/logout');
      session.clear();
      qc.clear();
      navigate('/login', { replace: true });
    },
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
